import { useMemo } from 'react';
import { AlertTriangle, CheckCircle2, Mic, Play, Sparkles } from 'lucide-react';
import type { AiInterviewResult, ReviewerScoreEdit } from '../../types';
import { effectiveScoreFor } from '../../types';
import { EditableScore } from '../EditableScore';

const QUALITATIVE_LABELS = ['Far below bar', 'Below bar', 'At bar', 'Above bar'];

/** Map raw AI verdict strings (STRONG_FIT, FAIL, REVIEW) to display labels. */
function humanizeVerdict(v: string): string {
    const upper = v.toUpperCase();
    if (upper === 'STRONG_FIT' || upper === 'PASS') return 'Strong match';
    if (upper === 'FAIL' || upper === 'NOT_A_FIT' || upper === 'REJECT') return 'Not a fit';
    if (upper === 'REVIEW' || upper === 'NEEDS_REVIEW' || upper === 'PARKING_LOT') return 'Needs review';
    return v.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

export interface AIInterviewTabProps {
    result: AiInterviewResult;
    jobId?: string;
    taskId?: string;
    onEdited?: (edit: ReviewerScoreEdit) => void;
}

export function AIInterviewTab({ result, jobId, taskId, onEdited }: AIInterviewTabProps) {
    const requirements = result.perRequirement ?? [];
    const competencies = result.perCriterion ?? [];

    // The "qualitative fit" 4-category grid. We synthesise four buckets from
    // the first 4 competencies if available, otherwise we hide the section.
    const qualitative = competencies.slice(0, 4);

    // Always derive the headline as a 0–100 percentage from the per-competency
    // scores (with reviewer edits applied). Reading `result.overallScore`
    // directly would (a) display whatever scale the backend persisted
    // (sometimes a raw sum, e.g. 14/15), and (b) ignore per-competency edits,
    // so the headline wouldn't move when the user edits an individual score.
    const headlineScore = useMemo(() => {
        if (competencies.length > 0) {
            let total = 0;
            let max = 0;
            for (const c of competencies) {
                const edit = effectiveScoreFor(result.reviewerFeedback, c.criterionId);
                total += edit ? edit.editedScore : c.score;
                max += c.scoreMax;
            }
            if (max > 0) return Math.round((total / max) * 100);
        }
        const overallEdit = effectiveScoreFor(result.reviewerFeedback, 'overall');
        if (overallEdit) return overallEdit.editedScore;
        if (typeof result.overallScore === 'number') return result.overallScore;
        return 0;
    }, [result.overallScore, result.reviewerFeedback, competencies]);

    const greenFlags = requirements.filter((r) => r.isFulfilled).map((r) => r.label).slice(0, 4);
    const redFlags = requirements.filter((r) => !r.isFulfilled).map((r) => r.label).slice(0, 4);

    return (
        <div className="flex flex-col">
            {/* Hero */}
            <div className="px-5 py-5" style={{ borderBottom: '1px solid var(--border-muted)' }}>
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <div
                            className="text-[10.5px] font-semibold uppercase tracking-[0.08em] mb-1.5"
                            style={{ color: 'var(--text-tertiary)' }}
                        >
                            AI Interview Score
                        </div>
                        <div className="flex items-center gap-2">
                            <span
                                className="text-[42px] leading-none tabular-nums"
                                style={{
                                    fontFamily: 'var(--v2-font-serif)',
                                    fontWeight: 500,
                                    color: 'var(--text-primary)',
                                }}
                            >
                                {headlineScore}
                            </span>
                            <span
                                className="text-[14px] tabular-nums"
                                style={{
                                    color: 'var(--text-tertiary)',
                                    fontFamily: 'var(--v2-font-serif)',
                                }}
                            >
                                / 100
                            </span>
                        </div>
                        {result.verdict && (
                            <div
                                className="text-[13.5px] mt-2 inline-flex items-center gap-1.5 whitespace-nowrap"
                                style={{ color: 'var(--text-secondary)' }}
                            >
                                <Sparkles
                                    size={12}
                                    strokeWidth={2}
                                    style={{ color: 'var(--accent)' }}
                                />
                                Verdict: <strong style={{ color: 'var(--text-primary)' }}>{humanizeVerdict(result.verdict)}</strong>
                            </div>
                        )}
                    </div>
                    <button
                        className="flex items-center gap-1.5 h-9 px-4 rounded-full text-[13px] font-medium"
                        style={{ background: 'var(--accent)', color: 'white' }}
                        type="button"
                    >
                        <Play size={13} strokeWidth={2} fill="white" />
                        Watch recording
                    </button>
                </div>
            </div>

            {/* Green / Red flags */}
            {(greenFlags.length > 0 || redFlags.length > 0) && (
                <div
                    className="grid grid-cols-2 gap-px"
                    style={{
                        background: 'var(--border-muted)',
                        borderBottom: '1px solid var(--border-muted)',
                    }}
                >
                    <FlagsCard title="Green flags" tone="success" items={greenFlags} icon={CheckCircle2} />
                    <FlagsCard title="Red flags" tone="danger" items={redFlags} icon={AlertTriangle} />
                </div>
            )}

            {/* Qualitative fit grid */}
            {qualitative.length > 0 && (
                <div className="px-5 py-4" style={{ borderBottom: '1px solid var(--border-muted)' }}>
                    <div
                        className="text-[10.5px] font-semibold uppercase tracking-[0.08em] mb-2"
                        style={{ color: 'var(--text-tertiary)' }}
                    >
                        Qualitative fit
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        {qualitative.map((c) => (
                            <div
                                key={c.criterionId}
                                className="px-3 py-2 rounded-md"
                                style={{
                                    background: 'var(--surface-secondary)',
                                    border: '1px solid var(--border-default)',
                                }}
                                data-editable-score-host
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <div
                                            className="text-[13.5px] font-medium leading-snug"
                                            style={{ color: 'var(--text-primary)' }}
                                        >
                                            {c.label}
                                        </div>
                                        {c.justification && (
                                            <div
                                                className="text-[12.5px] mt-1 line-clamp-2 leading-snug"
                                                style={{ color: 'var(--text-secondary)' }}
                                            >
                                                {c.justification}
                                            </div>
                                        )}
                                    </div>
                                    <EditableScore
                                        reportKind="ai_interview"
                                        resultId={result._id}
                                        componentId={c.criterionId}
                                        componentLabel={c.label}
                                        scale={{
                                            min: 1,
                                            max: 4,
                                            labels: QUALITATIVE_LABELS,
                                        }}
                                        aiScore={Math.max(1, Math.min(4, Math.round((c.score / c.scoreMax) * 4)))}
                                        aiJustification={c.justification}
                                        edits={result.reviewerFeedback}
                                        size="sm"
                                        jobId={jobId}
                                        taskId={taskId}
                                        onEdited={onEdited}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Competency scores */}
            {competencies.length > 0 && (
                <>
                    <div
                        className="px-5 py-2 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.08em]"
                        style={{
                            color: 'var(--text-tertiary)',
                            background: 'var(--surface-secondary)',
                            borderBottom: '1px solid var(--border-muted)',
                        }}
                    >
                        <Mic size={11} strokeWidth={2} />
                        Competency scores
                    </div>
                    {competencies.map((c) => (
                        <div
                            key={c.criterionId}
                            className="px-5 py-3.5"
                            style={{ borderBottom: '1px solid var(--border-muted)' }}
                            data-editable-score-host
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div
                                        className="text-[14px] font-medium leading-snug"
                                        style={{ color: 'var(--text-primary)' }}
                                    >
                                        {c.label}
                                    </div>
                                    {c.justification && (
                                        <div
                                            className="text-[13px] mt-1.5 leading-relaxed"
                                            style={{ color: 'var(--text-secondary)' }}
                                        >
                                            {c.justification}
                                        </div>
                                    )}
                                </div>
                                <EditableScore
                                    reportKind="ai_interview"
                                    resultId={result._id}
                                    componentId={c.criterionId}
                                    componentLabel={c.label}
                                    scale={{ min: 1, max: c.scoreMax }}
                                    aiScore={c.score}
                                    aiJustification={c.justification}
                                    edits={result.reviewerFeedback}
                                    size="md"
                                    jobId={jobId}
                                    taskId={taskId}
                                    onEdited={onEdited}
                                />
                            </div>
                        </div>
                    ))}
                </>
            )}

            {competencies.length === 0 && requirements.length === 0 && (
                <div
                    className="px-5 py-12 text-center text-[13px]"
                    style={{ color: 'var(--text-tertiary)' }}
                >
                    <Mic
                        size={20}
                        strokeWidth={1.5}
                        style={{ display: 'inline', marginBottom: 8, color: 'var(--text-quaternary)' }}
                    />
                    <div>No competencies or requirements analysed yet.</div>
                </div>
            )}
        </div>
    );
}

function FlagsCard({
    title,
    tone,
    items,
    icon: Icon,
}: {
    title: string;
    tone: 'success' | 'danger';
    items: string[];
    icon: React.ComponentType<{
        size?: number;
        strokeWidth?: number;
        style?: React.CSSProperties;
        className?: string;
    }>;
}) {
    const fg = tone === 'success' ? 'var(--fg-success)' : 'var(--fg-danger)';
    return (
        <div className="px-5 py-4" style={{ background: 'var(--surface-primary)' }}>
            <div
                className="text-[11px] font-semibold uppercase tracking-[0.08em] mb-2"
                style={{ color: fg }}
            >
                {title}
            </div>
            {items.length === 0 ? (
                <p className="text-[12px]" style={{ color: 'var(--text-quaternary)' }}>
                    None noted.
                </p>
            ) : (
                <ul className="flex flex-col gap-2">
                    {items.map((item) => (
                        <li
                            key={item}
                            className="flex items-start gap-1.5 text-[13.5px] leading-snug"
                            style={{ color: 'var(--text-primary)' }}
                        >
                            <Icon
                                size={12}
                                strokeWidth={2}
                                style={{ color: fg, marginTop: 2 }}
                                className="shrink-0"
                            />
                            {item}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
